import { create } from '@bufbuild/protobuf'
import * as Sentry from '@sentry/react'

import { SupportClientContextSchema } from '@/gen/cortado/v1/support_pb'
import type { SupportClientContext } from '@/gen/cortado/v1/support_pb'
import { redactSensitiveUrl } from './observability'

/**
 * Where the user is, and which observability session they are in, right now.
 * Attached to outbound support messages so staff can open the exact page and
 * pull the matching Sentry trace and session replay.
 *
 * Every field is best-effort: anything that cannot be determined comes back as
 * an empty string rather than throwing, because losing context must never stop
 * a user from reporting a problem.
 */
export interface SupportClientContextValues {
  pageUrl: string
  userAgent: string
  sentryTraceId: string
  sentryReplayId: string
}

/**
 * The current page URL, with sensitive query parameters redacted.
 *
 * Capture this at the moment of the action it describes — a screenshot's URL is
 * the URL at capture time, not at submit time, because the support panel stays
 * open while the user navigates.
 */
export function currentPageUrl(): string {
  if (typeof window === 'undefined' || !window.location?.href) return ''
  return redactSensitiveUrl(window.location.href)
}

export function getSupportClientContext(): SupportClientContextValues {
  return {
    pageUrl: currentPageUrl(),
    userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent || '',
    sentryTraceId: currentSentryTraceId(),
    sentryReplayId: currentSentryReplayId(),
  }
}

export function createSupportClientContext(): SupportClientContext {
  return create(SupportClientContextSchema, getSupportClientContext())
}

/**
 * The trace the SDK would stamp on an error raised right now: the active span's
 * trace when one is running, otherwise the scope's propagation context.
 */
function currentSentryTraceId(): string {
  try {
    const activeSpan = Sentry.getActiveSpan()
    if (activeSpan) {
      const traceId = Sentry.spanToJSON(activeSpan).trace_id
      if (traceId) return traceId
    }
    return Sentry.getCurrentScope().getPropagationContext().traceId || ''
  } catch {
    return ''
  }
}

/**
 * Only set while a replay is actually recording — replays are sampled, so an
 * empty id here is normal rather than a failure.
 */
function currentSentryReplayId(): string {
  try {
    return Sentry.getReplay()?.getReplayId() || ''
  } catch {
    return ''
  }
}
